import { Zip, ZipPassThrough } from "fflate";
import streamSaver from "streamsaver";
import {
    getDeliveryApiBase,
    getFileDownloadUrl,
    getFileProxyDownloadUrl,
    getProxyDownloadUrl,
    isFolderItem,
    listDeliveryFolder,
} from "./deliveryApi";
import { toFolderPrefix } from "./deliveryPath";
import { getTranslator } from "../i18n";

let streamSaverConfigured = false;

function getPublicUrlPrefix() {
    const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
    if (!base || base === ".") {
        return "";
    }
    return base;
}

export function configureStreamSaver() {
    if (streamSaverConfigured) {
        return;
    }
    streamSaver.mitm = `${window.location.origin}${getPublicUrlPrefix()}/mitm.html`;
    streamSaverConfigured = true;
}

export function isStreamingDownloadSupported() {
    return (
        typeof WritableStream !== "undefined" &&
        typeof window !== "undefined" &&
        window.isSecureContext !== false
    );
}

function triggerUrlDownload(url, filename) {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "download";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();
}

function safeZipFilename(name) {
    const cleaned = String(name || "album")
        .replace(/[^\w\u4e00-\u9fff.\- ()]+/g, "_")
        .trim();
    return `${(cleaned || "album").slice(0, 80)}.zip`;
}

function uniqueZipPath(path, usedPaths) {
    const base = path || "file";
    if (!usedPaths.has(base)) {
        usedPaths.add(base);
        return base;
    }

    const slashIndex = base.lastIndexOf("/");
    const dir = slashIndex >= 0 ? base.slice(0, slashIndex + 1) : "";
    const filename = slashIndex >= 0 ? base.slice(slashIndex + 1) : base;
    const dotIndex = filename.lastIndexOf(".");
    const stem = dotIndex > 0 ? filename.slice(0, dotIndex) : filename;
    const ext = dotIndex > 0 ? filename.slice(dotIndex) : "";

    let index = 2;
    let candidate = `${dir}${stem} (${index})${ext}`;
    while (usedPaths.has(candidate)) {
        index += 1;
        candidate = `${dir}${stem} (${index})${ext}`;
    }

    usedPaths.add(candidate);
    return candidate;
}

async function* walkDeliveryFolder(folderId, rootPrefix, pathPrefix = "") {
    let pageToken = null;

    do {
        const data = await listDeliveryFolder(toFolderPrefix(folderId), {
            rootPrefix,
            pageToken,
        });

        for (const item of data.files || []) {
            if (isFolderItem(item.mimeType)) {
                yield* walkDeliveryFolder(item.id, rootPrefix, `${pathPrefix}${item.name}/`);
            } else {
                yield {
                    id: item.id,
                    name: item.name,
                    path: `${pathPrefix}${item.name}`,
                    downloadUrl: item.downloadUrl,
                    proxyDownloadUrl: item.proxyDownloadUrl,
                };
            }
        }

        pageToken = data.nextPageToken || null;
    } while (pageToken);
}

async function* iterateDownloadEntries({ files = [], folders = [] } = {}, { rootPrefix } = {}) {
    const seen = new Set();

    for (const file of files) {
        if (seen.has(file.id)) {
            continue;
        }
        seen.add(file.id);
        yield {
            id: file.id,
            name: file.name,
            path: file.path || file.name,
            downloadUrl: file.downloadUrl,
            proxyDownloadUrl: file.proxyDownloadUrl,
        };
    }

    for (const folder of folders) {
        const prefix = folder.pathPrefix ?? `${folder.name}/`;

        for await (const entry of walkDeliveryFolder(folder.id, rootPrefix, prefix)) {
            if (seen.has(entry.id)) {
                continue;
            }
            seen.add(entry.id);
            yield entry;
        }
    }
}

export async function buildDownloadManifest({ files = [], folders = [] } = {}, { rootPrefix } = {}) {
    const usedPaths = new Set();
    const manifest = [];

    for await (const entry of iterateDownloadEntries({ files, folders }, { rootPrefix })) {
        manifest.push({
            ...entry,
            downloadUrl: entry.downloadUrl || getFileDownloadUrl({ id: entry.id }),
            proxyDownloadUrl: entry.proxyDownloadUrl || getProxyDownloadUrl(entry.id, rootPrefix),
            zipPath: uniqueZipPath(entry.path, usedPaths),
        });
    }

    return manifest;
}

export async function countDownloadEntries(selection, options = {}) {
    return (await buildDownloadManifest(selection, options)).length;
}

async function pumpResponseToZipEntry(response, zipEntry, signal, checkFatal) {
    if (!response.body) {
        checkFatal?.();
        const buffer = new Uint8Array(await response.arrayBuffer());
        checkFatal?.();
        zipEntry.push(buffer, true);
        return;
    }

    const reader = response.body.getReader();
    try {
        while (true) {
            if (signal?.aborted) {
                throw new Error(getTranslator()("delivery.downloadErrors.cancelled"));
            }
            checkFatal?.();

            const { done, value } = await reader.read();
            if (done) {
                zipEntry.push(new Uint8Array(0), true);
                break;
            }
            zipEntry.push(value, false);
            // 写盘链路（fflate 编码 / streamSaver 落盘）一旦出错，checkFatal 会立即抛出，
            // 中断当前文件剩余分片的下载，而不是读完整个响应体后才在外层发现问题。
            checkFatal?.();
        }
    } finally {
        reader.releaseLock();
    }
}

// 直连 R2 优先（走 CDN，通常最快），fetch() 本身失败（网络错误 / 被 CORS 拦截）时
// 自动退避到 Worker 代理地址重试一次。HTTP 状态码错误（4xx/5xx）不在这里处理，
// 两条路径大概率会得到一样的错误，交给调用方按 response.ok 统一判断。
async function fetchDirectThenProxy(primaryUrl, proxyUrl, signal, context = {}) {
    if (!primaryUrl && !proxyUrl) {
        throw new Error(getTranslator()("delivery.downloadErrors.missingUrl"));
    }

    if (!primaryUrl) {
        return fetch(proxyUrl, { signal });
    }

    try {
        return await fetch(primaryUrl, { signal });
    } catch (error) {
        if (!proxyUrl) {
            throw error;
        }
        logDelivery("warn", "direct R2 fetch failed, falling back to worker proxy", {
            ...context,
            primaryUrl,
            proxyUrl,
            error: error.message,
        });
        return fetch(proxyUrl, { signal });
    }
}

async function addFileToZip(zip, zipPath, downloadUrl, proxyDownloadUrl, signal, context = {}, checkFatal) {
    checkFatal?.();
    const response = await fetchFileResponseWithRetry(downloadUrl, proxyDownloadUrl, signal, {
        ...context,
        zipPath,
    });
    checkFatal?.();
    const entry = new ZipPassThrough(zipPath);
    zip.add(entry);
    await pumpResponseToZipEntry(response, entry, signal, checkFatal);
}

// 直接把整个响应体读进 Blob（`response.blob()`）会把整份文件都留在内存里，
// 大视频/大图在移动端浏览器上容易触发内存告警甚至标签页崩溃。这里优先把响应体
// 边下边写入磁盘（借助 zip 下载路径已经在用的 streamSaver），只有在当前环境不
// 支持流式写盘时才退回到整包 Blob 的旧方案。
export async function streamResponseToFile(response, filename, signal) {
    configureStreamSaver();

    const sizeHeader = Number(response.headers.get("Content-Length"));
    const fileStream = streamSaver.createWriteStream(filename || "download", {
        size: Number.isFinite(sizeHeader) && sizeHeader > 0 ? sizeHeader : undefined,
    });

    const throwIfAborted = () => {
        if (signal?.aborted) {
            throw new Error(getTranslator()("delivery.downloadErrors.cancelled"));
        }
    };

    if (response.body?.pipeTo) {
        try {
            await response.body.pipeTo(fileStream, signal ? { signal } : undefined);
        } catch (error) {
            throwIfAborted();
            throw error;
        }
        return;
    }

    const writer = fileStream.getWriter();
    try {
        if (!response.body) {
            const buffer = new Uint8Array(await response.arrayBuffer());
            await writer.write(buffer);
            await writer.close();
            return;
        }

        const reader = response.body.getReader();
        try {
            while (true) {
                throwIfAborted();
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }
                await writer.write(value);
            }
            await writer.close();
        } finally {
            reader.releaseLock();
        }
    } catch (error) {
        await writer.abort(error).catch(() => {});
        throw error;
    }
}

export async function downloadDeliveryFile(file, { signal } = {}) {
    const primaryUrl = getFileDownloadUrl(file);
    const proxyUrl = getFileProxyDownloadUrl(file);
    if (!primaryUrl && !proxyUrl) {
        throw new Error(getTranslator()("delivery.downloadErrors.noDownloadUrl"));
    }

    const response = await fetchDirectThenProxy(primaryUrl, proxyUrl, signal, { name: file.name });
    if (!response.ok) {
        throw new Error(getTranslator()("delivery.downloadErrors.httpFailed", { status: response.status }));
    }

    if (isStreamingDownloadSupported()) {
        await streamResponseToFile(response, file.name || "download", signal);
        return;
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    try {
        triggerUrlDownload(blobUrl, file.name || "download");
    } finally {
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    }
}

const SINGLE_DOWNLOAD_GAP_MS = 400;
const FILE_FETCH_MAX_ATTEMPTS = 3;
const FILE_FETCH_RETRY_DELAY_MS = 400;
const FAILED_FILES_REPORT_NAME = "_download-failures.txt";

function delay(ms) {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

function logDelivery(level, message, data) {
    const payload = { ts: new Date().toISOString(), ...data };
    console[level](`[delivery-download] ${message}`, payload);
}

async function readHttpErrorDetail(response) {
    const contentType = response.headers.get("Content-Type") || "";
    let bodyText = "";

    try {
        bodyText = await response.text();
    } catch {
        bodyText = "";
    }

    const detail = {
        status: response.status,
        statusText: response.statusText,
        contentType,
        bodyPreview: bodyText.slice(0, 500),
    };

    if (contentType.includes("json") || bodyText.trimStart().startsWith("{")) {
        try {
            const json = JSON.parse(bodyText);
            const apiError = json.error || json;
            detail.message = apiError.message;
            detail.reason = apiError.reason || apiError.errors?.[0]?.reason;
            detail.domain = apiError.domain || apiError.errors?.[0]?.domain;
            detail.code = apiError.code;
            detail.key = apiError.key;
            detail.raw = bodyText.slice(0, 1000);
        } catch {
            detail.message = bodyText.slice(0, 500) || response.statusText;
        }
    } else {
        detail.message = bodyText.slice(0, 500) || response.statusText;
    }

    return detail;
}

function formatFetchErrorMessage(detail) {
    const parts = [`HTTP ${detail.status}`];
    if (detail.message) {
        parts.push(detail.message);
    }
    if (detail.reason) {
        parts.push(`reason=${detail.reason}`);
    }
    if (detail.domain) {
        parts.push(`domain=${detail.domain}`);
    }
    return parts.join(" | ");
}

function formatFailedFilesReport(failed, zipName) {
    const t = getTranslator();
    const timestamp = new Date().toISOString();
    const lines = [
        t("delivery.downloadErrors.failureReportTitle"),
        t("delivery.downloadErrors.failureReportAlbum", { name: zipName || "album" }),
        t("delivery.downloadErrors.failureReportGenerated", { time: timestamp }),
        t("delivery.downloadErrors.failureReportCount", { count: failed.length }),
        "",
    ];

    failed.forEach((item, index) => {
        lines.push(`${index + 1}. ${item.zipPath}`);
        if (item.name && item.name !== item.zipPath) {
            lines.push(`   ${t("delivery.downloadErrors.failureReportName", { name: item.name })}`);
        }
        if (item.id) {
            lines.push(`   ${t("delivery.downloadErrors.failureReportKey", { key: item.id })}`);
        }
        lines.push(`   ${t("delivery.downloadErrors.failureReportError", { error: item.error })}`);
        if (item.reason) {
            lines.push(`   ${t("delivery.downloadErrors.failureReportReason", { reason: item.reason })}`);
        }
        if (item.domain) {
            lines.push(`   ${t("delivery.downloadErrors.failureReportDomain", { domain: item.domain })}`);
        }
        if (item.index != null) {
            lines.push(
                `   ${t("delivery.downloadErrors.failureReportIndex", {
                    index: item.index,
                    total: item.total,
                })}`
            );
        }
        lines.push(`   ${t("delivery.downloadErrors.failureReportAttempts", { attempts: item.attempts })}`);
        if (item.bodyPreview) {
            lines.push(`   ${t("delivery.downloadErrors.failureReportBody", { body: item.bodyPreview })}`);
        }
        lines.push("");
    });

    return `${lines.join("\n").trim()}\n`;
}

function addStringToZip(zip, zipPath, text) {
    const entry = new ZipPassThrough(zipPath);
    zip.add(entry);
    entry.push(new TextEncoder().encode(`\uFEFF${text}`), true);
}

async function fetchFileResponse(downloadUrl, proxyDownloadUrl, signal, context = {}) {
    const response = await fetchDirectThenProxy(downloadUrl, proxyDownloadUrl, signal, context);

    if (!response.ok) {
        const detail = await readHttpErrorDetail(response);
        logDelivery("error", "file fetch failed", {
            ...context,
            downloadUrl,
            ...detail,
        });
        const error = new Error(formatFetchErrorMessage(detail));
        error.detail = detail;
        throw error;
    }

    if (context.index != null) {
        logDelivery("log", "file fetch ok", {
            index: context.index,
            total: context.total,
            zipPath: context.zipPath,
            downloadUrl,
        });
    }

    return response;
}

async function fetchFileResponseWithRetry(downloadUrl, proxyDownloadUrl, signal, context = {}) {
    let lastError = null;

    for (let attempt = 1; attempt <= FILE_FETCH_MAX_ATTEMPTS; attempt += 1) {
        if (signal?.aborted) {
            throw new Error(getTranslator()("delivery.downloadErrors.cancelled"));
        }

        try {
            return await fetchFileResponse(downloadUrl, proxyDownloadUrl, signal, {
                ...context,
                attempt,
            });
        } catch (error) {
            lastError = error;
            logDelivery("warn", "file fetch retry", {
                ...context,
                downloadUrl,
                attempt,
                maxAttempts: FILE_FETCH_MAX_ATTEMPTS,
                error: error.message,
                detail: error.detail,
            });
            if (attempt < FILE_FETCH_MAX_ATTEMPTS) {
                await delay(FILE_FETCH_RETRY_DELAY_MS * attempt);
            }
        }
    }

    throw lastError || new Error(getTranslator()("delivery.downloadErrors.downloadFailed"));
}

export async function downloadFilesIndividually(files, { onProgress, signal } = {}) {
    if (!files?.length) {
        throw new Error(getTranslator()("delivery.downloadErrors.noFiles"));
    }

    const total = files.length;
    const failed = [];
    let succeeded = 0;

    for (let index = 0; index < files.length; index += 1) {
        if (signal?.aborted) {
            throw new Error(getTranslator()("delivery.downloadErrors.cancelled"));
        }

        const file = files[index];

        try {
            await downloadDeliveryFile(file, { signal });
            succeeded += 1;
            onProgress?.({
                current: index + 1,
                total,
                name: file.name,
                failedCount: failed.length,
                succeededCount: succeeded,
            });
        } catch (error) {
            if (signal?.aborted) {
                throw error;
            }

            logDelivery("error", "individual file download failed", {
                index: index + 1,
                total,
                name: file.name,
                fileKey: file.id,
                error: error.message,
            });

            failed.push({
                name: file.name,
                id: file.id,
                error: error.message || getTranslator()("delivery.downloadErrors.downloadFailed"),
                index: index + 1,
                total,
            });

            onProgress?.({
                current: index + 1,
                total,
                name: file.name,
                failedCount: failed.length,
                succeededCount: succeeded,
            });
        }

        if (index < files.length - 1) {
            await new Promise((resolve) => {
                setTimeout(resolve, SINGLE_DOWNLOAD_GAP_MS);
            });
        }
    }

    return { failed, succeeded, total };
}

export async function downloadManifestAsStreamingZip(
    manifest,
    { zipName = "album", onProgress, signal } = {}
) {
    if (!getDeliveryApiBase()) {
        throw new Error(getTranslator()("delivery.downloadErrors.apiNotConfigured"));
    }

    configureStreamSaver();

    if (!isStreamingDownloadSupported()) {
        throw new Error(getTranslator()("delivery.downloadErrors.streamingUnsupported"));
    }

    if (!manifest?.length) {
        throw new Error(getTranslator()("delivery.downloadErrors.noFiles"));
    }

    const fileStream = streamSaver.createWriteStream(safeZipFilename(zipName));
    const writer = fileStream.getWriter();

    let writeChain = Promise.resolve();
    let zipError = null;
    // writer.write() 失败（磁盘配额不足、用户关闭系统保存对话框等）只会让 writeChain
    // 变成一个被拒绝的 Promise，如果只在最后统一 await，中途还会继续无意义地抓取
    // 剩余所有文件。这里把每次写入的失败结果同步记录到 writeError，配合下面循环里
    // 的 checkFatal，让抓取尽快中止。
    let writeError = null;

    const recordWriteError = (error) => {
        if (!writeError) {
            writeError = error;
        }
        throw error;
    };

    const checkFatal = () => {
        if (zipError || writeError) {
            throw zipError || writeError;
        }
    };

    const zip = new Zip((err, chunk, final) => {
        if (err) {
            zipError = err;
            return;
        }

        if (chunk?.length) {
            writeChain = writeChain.then(() => writer.write(chunk)).catch(recordWriteError);
        }

        if (final) {
            writeChain = writeChain.then(() => writer.close()).catch(recordWriteError);
        }
    });

    const failed = [];
    let succeeded = 0;

    try {
        const total = manifest.length;

        for (let index = 0; index < manifest.length; index += 1) {
            if (signal?.aborted) {
                throw new Error(getTranslator()("delivery.downloadErrors.cancelled"));
            }
            checkFatal();

            const entry = manifest[index];
            onProgress?.({
                phase: "fetching",
                current: index + 1,
                total,
                path: entry.zipPath,
                failedCount: failed.length,
                succeededCount: succeeded,
            });

            try {
                await addFileToZip(
                    zip,
                    entry.zipPath,
                    entry.downloadUrl,
                    entry.proxyDownloadUrl,
                    signal,
                    { index: index + 1, total },
                    checkFatal
                );
                checkFatal();
                // 等到本文件已写入（或写入失败）的分片都落定，再去抓下一个文件：
                // 既能给写盘做背压，也能让磁盘写入失败尽快中断后续抓取，而不是拖到
                // 整个 manifest 抓完才在最后一次 await writeChain 时才发现。
                await writeChain;
                succeeded += 1;
            } catch (error) {
                if (signal?.aborted) {
                    throw error;
                }

                if (zipError || writeError) {
                    throw zipError || writeError;
                }

                const detail = error.detail || {};
                logDelivery("error", "file skipped after retries", {
                    index: index + 1,
                    total,
                    zipPath: entry.zipPath,
                    fileKey: entry.id,
                    error: error.message,
                    ...detail,
                });

                failed.push({
                    zipPath: entry.zipPath,
                    name: entry.name,
                    id: entry.id,
                    error: error.message || getTranslator()("delivery.downloadErrors.downloadFailed"),
                    reason: detail.reason,
                    domain: detail.domain,
                    bodyPreview: detail.bodyPreview,
                    index: index + 1,
                    total,
                    attempts: FILE_FETCH_MAX_ATTEMPTS,
                });

                onProgress?.({
                    phase: "retrying",
                    current: index + 1,
                    total,
                    path: entry.zipPath,
                    failedCount: failed.length,
                    succeededCount: succeeded,
                });
            }
        }

        if (failed.length > 0) {
            addStringToZip(
                zip,
                FAILED_FILES_REPORT_NAME,
                formatFailedFilesReport(failed, zipName)
            );
        }

        zip.end();
        await writeChain;
        checkFatal();

        if (failed.length > 0) {
            logDelivery("error", "zip completed with failures", {
                zipName,
                succeeded,
                failed: failed.length,
                total,
                firstFailure: failed[0],
            });
        } else {
            logDelivery("log", "zip completed", { zipName, total, succeeded });
        }

        onProgress?.({
            phase: "done",
            total,
            failedCount: failed.length,
            succeededCount: succeeded,
            failed,
        });

        return { failed, succeeded, total };
    } catch (error) {
        zip.terminate();
        try {
            await writer.abort(error);
        } catch {
            // ignore abort errors
        }
        throw error;
    }
}

export async function downloadEntriesAsStreamingZip({
    files = [],
    folders = [],
    zipName = "album",
    rootPrefix,
    onProgress,
    signal,
} = {}) {
    onProgress?.({ phase: "listing" });
    const manifest = await buildDownloadManifest({ files, folders }, { rootPrefix });
    return downloadManifestAsStreamingZip(manifest, { zipName, onProgress, signal });
}

export async function downloadFolderAsZip(folder, options = {}) {
    return downloadEntriesAsStreamingZip({
        folders: [
            {
                id: folder.id,
                name: folder.name,
                pathPrefix: folder.pathPrefix ?? "",
            },
        ],
        zipName: folder.name,
        ...options,
    });
}

export async function collectFolderFiles(folderId, rootPrefix) {
    const files = [];
    for await (const entry of walkDeliveryFolder(folderId, rootPrefix)) {
        files.push(entry);
    }
    return files;
}
